import {
  Tag as ReactAriaTag,
  TagGroup as ReactAriaTagGroup,
  TagList as ReactAriaTagList,
} from "react-aria-components";
import type {
  TagGroupProps as ReactAriaTagGroupProps,
  TagListProps as ReactAriaTagListProps,
  TagProps as ReactAriaTagProps,
} from "react-aria-components";
import { FieldError } from "react-hook-form";

import { useI18n } from "../use-i18n";
import { cn, cv, type VariantProps } from "../utils";
import {
  NcButton,
  NcFieldLayout,
  NcFieldLayoutProps,
  NcFormattedMessage,
  NcIconCross,
  NcLabel,
} from "./index";

export const ncTagGroupClasses = {
  list: "flex flex-wrap gap-2 data-focus-visible:outline-main data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
  tag: cv(
    "text-light inline-flex items-center rounded-full align-baseline font-bold data-focus-visible:outline-main data-focus-visible:outline-2 data-focus-visible:outline-offset-2",
    {
      variants: {
        variant: {
          default: "bg-main",
          neutral: "bg-ui-dark",
          success: "bg-success",
          danger: "bg-danger",
        },
      },
      defaultVariants: {
        variant: "default",
      },
    }
  ),
  tagContent: "inline-flex gap-1.5 px-3 py-2",
};

// NOTE: React Aria also supports single and multiple tag selection that could be supported
interface NcTagGroupProps<T>
  extends Omit<
      ReactAriaTagGroupProps,
      "children" | "selectionMode" | "selectedKeys" | "defaultSelectedKeys"
    >,
    Pick<ReactAriaTagListProps<T>, "items" | "children"> {
  label: string;
  hideLabel?: boolean;
  description?: string;
  emptyMessage?: string;
  error?: FieldError;
  variant?: NcFieldLayoutProps["variant"];
}

export const NcTagGroup = <T extends object>({
  children,
  description,
  items,
  label,
  hideLabel,
  variant,
  error,
  emptyMessage,
  className,
  ...props
}: NcTagGroupProps<T>) => {
  const { t } = useI18n();
  const emptyText = typeof emptyMessage === "string" ? emptyMessage : t("None");
  return (
    <ReactAriaTagGroup {...props}>
      <NcFieldLayout variant={variant}>
        <NcLabel hideLabel={hideLabel}>{label}</NcLabel>
        <ReactAriaTagList
          className={cn(ncTagGroupClasses.list, className)}
          items={items}
          renderEmptyState={() => (
            <NcFormattedMessage variant="secondary">{emptyText}</NcFormattedMessage>
          )}
        >
          {children}
        </ReactAriaTagList>
        {description && <NcFieldLayout.Description>{description}</NcFieldLayout.Description>}
        <NcFieldLayout.ErrorMessage>{error?.message}</NcFieldLayout.ErrorMessage>
      </NcFieldLayout>
    </ReactAriaTagGroup>
  );
};

interface NcTagGroupTagProps
  extends ReactAriaTagProps,
    VariantProps<typeof ncTagGroupClasses.tag> {}

const NcTagGroupTag = ({
  children,
  variant = "default",
  className,
  ...props
}: NcTagGroupTagProps) => {
  const textValue = typeof children === "string" ? children : undefined;
  return (
    <ReactAriaTag
      data-nc="NcTagGroup.Tag"
      textValue={textValue}
      className={cn(ncTagGroupClasses.tag({ variant }), className)}
      {...props}
    >
      {({ allowsRemoving }) => (
        <>
          <span className={ncTagGroupClasses.tagContent}>
            <>{children}</>
          </span>
          {allowsRemoving && (
            <NcButton
              className="-ml-3 px-3 py-2 data-disabled:hidden"
              variant="trigger"
              slot="remove"
            >
              <NcIconCross />
            </NcButton>
          )}
        </>
      )}
    </ReactAriaTag>
  );
};

NcTagGroup.Tag = NcTagGroupTag;
